/**
 * nen5740.ts — onderzoeksstrategieën uit NEN 5740:2023+C1:2024 en wat ze vragen aan boringen,
 * peilbuizen en analyses.
 *
 * De aantallen zijn overgenomen uit de tabellen van de norm (tabel 2, 3, 6, 7, 8, 9, 10, 11 en 12; het
 * tabelnummer staat bij elke strategie). Boven 1 ha of bij een tracé geeft de norm formules met
 * p (oppervlakte in ha, of lengte gedeeld door 50, 100 of 200); die zijn hier letterlijk zo
 * uitgerekend en naar boven afgerond. Waar de norm een vast totaal noemt ("het totaal aantal
 * boringen moet steeds 10 + 10p bedragen") wordt het afronden daarop gecorrigeerd.
 *
 * De diepten volgen de paragraaf van de strategie en hangen af van de grondwaterstand, de
 * ontgravingsdiepte (tracés) of de kern/verdachte laag. Zonder opgave geldt wat de norm voor
 * 'onbekend' voorschrijft (bijv. peilbuis tot 5 m bij een onbekende grondwaterstand, tabel 2 b);
 * waar de norm niets zegt, staat de aanname als notitie bij het plan.
 *
 * Wat de norm aan de uitvoerder overlaat (combineren van deelstrategieën, extra boringen bij
 * zintuiglijke waarnemingen, plaatsing t.o.v. de stromingsrichting) zit hier niet in: dit is het
 * minimum volgens de norm (NEN 5740 § 4.2, opmerking 2).
 */

export type Nen5740Code = 'ONV-NL' | 'ONV-L' | 'VEP' | 'VEP-OO' | 'VED-HO-NL' | 'VED-HO-L' | 'VED-HE-NL' | 'VED-HE-L';

/** Hoe de boringen over de locatie verdeeld worden. */
export type PlanLayout = 'grid' | 'line' | 'source';

/** Waar de grootte van een locatie in gemeten wordt. */
export type PlanMeasure = 'area_m2' | 'length_m' | 'sources';

/** Opgaven die de diepten bepalen; alles in cm onder maaiveld, null = onbekend. */
export interface PlanParams {
  /** Grondwaterstand. */
  gwsCm?: number | null;
  /** Ontgravingsdiepte (tracés: boringen tot 0,25 m eronder). */
  excavationCm?: number | null;
  /** Bovenzijde van de verdachte laag (VED-HE). */
  suspectTopCm?: number | null;
  /** Onderzijde van de kern van bodembelasting of de verdachte laag (VEP, VED). */
  suspectBottomCm?: number | null;
  /** VEP-OO: inhoud van de tank, of van alle tanks samen als het er meer zijn (m³). */
  tankVolumeM3?: number | null;
  /** VEP-OO: aantal ondergrondse tanks op korte afstand van elkaar (1 = tabel 7, 2+ = tabel 8). */
  tankCount?: number | null;
  /** VEP-OO: onderzijde van de tank (cm -mv). */
  tankBottomCm?: number | null;
  /** VEP-OO: leiding tussen vulpunt, ontluchting of afleverinstallatie en de tank (m). */
  pipeLengthM?: number | null;
}

export type PlanParamKey = keyof PlanParams;

export interface PlanCounts {
  /** Boringen per einddiepte (cm -mv), zonder de boringen die een peilbuis worden. */
  borings: { depthCm: number; count: number; label?: string }[];
  /** Boringen met een peilbuis. */
  peilbuizen: number;
  /** Einddiepte van een peilbuisboring (cm -mv). */
  peilbuisDepthCm?: number | null;
  /** Te analyseren grond(meng)monsters, per laag zoals de tabel ze noemt. */
  soilAnalyses?: { label: string; count: number }[] | null;
  /** Te analyseren grondwatermonsters. */
  waterAnalyses?: number | null;
  /** Wat er uit de opgaven volgde, of welke aanname er gedaan is. */
  notes?: string[];
}

export interface StrategyRule {
  code: Nen5740Code;
  label: string;
  description: string;
  /** Paragraaf en tabel in NEN 5740:2023+C1:2024. */
  source: string;
  measure: PlanMeasure;
  layout: PlanLayout;
  /** Welke opgaven de diepten van deze strategie bepalen. */
  params: PlanParamKey[];
  /** Aantallen bij deze grootte (m² of m); een string is een reden waarom het niet kan. */
  compute: (size: number, params: PlanParams) => PlanCounts | string;
}

const op = (n: number) => Math.ceil(n - 1e-9);

/**
 * Componenten naar boven afronden, en als de norm een totaal voorschrijft het eerste onderdeel
 * (de ondiepe boringen) zo verlagen dat het totaal klopt.
 */
function rondAf(waarden: number[], totaal?: number): number[] {
  const uit = waarden.map(op);
  if (totaal != null) {
    const teveel = uit.reduce((s, x) => s + x, 0) - op(totaal);
    if (teveel > 0) uit[0] = Math.max(0, uit[0] - teveel);
  }
  return uit;
}

const known = (v: number | null | undefined): v is number => v != null && Number.isFinite(v);

/** Peilbuis: bovenkant filter 0,5 m onder de grondwaterspiegel, filter 1 m, max 6,5 m (§ 5.1.2.2 c). */
const peilbuisOpGws = (gws: number | null | undefined, onbekend: number) =>
  (known(gws) ? Math.min(gws + 150, 650) : onbekend);

/** Een tabel naar oppervlakte (ha): de eerste rij waarvan de bovengrens groot genoeg is. */
function rij(rows: [number, number[]][], ha: number): number[] | null {
  return rows.find(([tot]) => ha <= tot + 1e-12)?.[1] || null;
}

// ── Tabel 2 — ONV-NL ────────────────────────────────────────────────────
// Kolommen: boring tot 0,5 m · én boring tot grondwater · én boring met peilbuis ·
// analyses bovengrond · ondergrond · grondwater.
const TABEL_2: [number, number[]][] = [
  [0.01, [2, 0, 1, 1, 1, 1]], [0.05, [2, 1, 1, 1, 1, 1]], [0.10, [4, 1, 1, 1, 1, 1]], [0.15, [6, 1, 1, 1, 1, 1]],
  [0.20, [8, 2, 1, 2, 1, 1]], [0.30, [9, 2, 1, 2, 1, 1]], [0.40, [10, 2, 1, 2, 1, 1]], [0.50, [11, 3, 1, 2, 1, 1]],
  [0.70, [12, 3, 1, 2, 2, 1]], [0.90, [13, 4, 2, 3, 2, 2]], [1.00, [14, 4, 2, 3, 2, 2]],
];

function onvNl(m2: number, p: PlanParams): PlanCounts {
  const ha = m2 / 10000;
  let [b05, bGw, pb, boven, onder, gw] = ha <= 1
    ? rij(TABEL_2, ha)!
    : [...rondAf([7 + 7 * ha, 2 + 2 * ha, 1 + ha], 10 + 10 * ha), ...rondAf([2 + ha, 1 + ha, 1 + ha])];
  const notes: string[] = [];
  // Tabel 2 a: tot 0,5 m onder de grondwaterspiegel, minimaal 1,0 en maximaal 2,0 m.
  const gwsDiepte = known(p.gwsCm) ? Math.min(200, Math.max(100, p.gwsCm + 50)) : 200;
  if (!known(p.gwsCm)) notes.push('Grondwaterstand onbekend: boringen tot grondwater 2,0 m, peilbuizen 5 m (tabel 2 a en b).');
  // Tabel 2 b: grondwater dieper dan 5 m → geen peilbuizen, wel boren tot 2 m.
  if (known(p.gwsCm) && p.gwsCm > 500) {
    bGw += pb; pb = 0; gw = 0;
    notes.push('Grondwater dieper dan 5 m: geen peilbuizen; die boringen gaan tot 2 m (tabel 2 b).');
  }
  return {
    borings: [
      { depthCm: 50, count: b05, label: 'tot 0,5 m' },
      { depthCm: gwsDiepte, count: bGw, label: 'tot 0,5 m onder grondwater' },
    ].filter((b) => b.count > 0),
    peilbuizen: pb,
    peilbuisDepthCm: pb ? peilbuisOpGws(p.gwsCm, 500) : null,
    soilAnalyses: [{ label: 'bovengrond', count: boven }, { label: 'ondergrond', count: onder }],
    waterAnalyses: gw,
    notes,
  };
}

// ── Tracés (tabel 3, 10, 12) ────────────────────────────────────────────

/** Een tracé-rij: [tot lengte (m), deler voor p, vaste boringen, factor voor peilbuizen en analyses]. */
type TraceRow = [number, number, number, number];

function trace(rows: TraceRow[], tabel: string) {
  return (lengte: number, p: PlanParams): PlanCounts | string => {
    const r = rows.find(([tot]) => lengte <= tot);
    if (!r) return `Langer dan tabel ${tabel} gaat; splits het tracé.`;
    const [, deler, vast, f] = r;
    const pp = lengte / deler;
    const [b, pb, boven, onder, gw] = rondAf([vast + pp, 1 + f * pp, 1 + f * pp, 1 + f * pp, 1 + f * pp]);
    const ontgr = known(p.excavationCm) ? p.excavationCm : null;
    const bDiepte = (ontgr ?? 150) + 25;
    const notes = ontgr == null ? ['Ontgravingsdiepte onbekend: uitgegaan van 1,5 m. Vul hem in voor de juiste boordiepte.'] : [];
    // Grondwater dieper dan 0,25 m onder de ontgravingsdiepte: geen peilbuizen, boringen tot 0,25 m eronder.
    const geenGw = known(p.gwsCm) && p.gwsCm > bDiepte;
    if (geenGw) notes.push(`Grondwater dieper dan 0,25 m onder de ontgravingsdiepte: peilbuizen worden boringen (tabel ${tabel} a).`);
    return {
      borings: [{ depthCm: bDiepte, count: b + (geenGw ? pb : 0), label: 'tot 0,25 m onder ontgravingsdiepte' }],
      peilbuizen: geenGw ? 0 : pb,
      peilbuisDepthCm: geenGw ? null : peilbuisOpGws(p.gwsCm, Math.max(bDiepte + 100, 300)),
      soilAnalyses: [{ label: 'bovengrond', count: boven }, { label: 'ondergrond', count: onder }],
      waterAnalyses: geenGw ? 0 : gw,
      notes,
    };
  };
}

// ── Tabel 6 — VEP (per kern van bodembelasting) ─────────────────────────
// Kolommen: boring tot 0,5 m onder de kern · én boring met peilbuis · grond (kern) · grondwater.
const TABEL_6: [number, number[]][] = [
  [0.01, [2, 1, 1, 1]], [0.05, [3, 1, 1, 1]], [0.08, [4, 1, 1, 1]], [0.10, [5, 1, 1, 1]],
];

function vep(m2: number, p: PlanParams): PlanCounts | string {
  const r = rij(TABEL_6, m2 / 10000);
  if (!r) return 'Een kern groter dan 0,1 ha valt buiten tabel 6; bij een homogene belasting geldt VED-HO (tabel 6 c).';
  let [b, pb, grond, gw] = r;
  const notes: string[] = [];
  const kern = known(p.suspectBottomCm) ? p.suspectBottomCm : null;
  if (kern == null) notes.push('Onderzijde van de kern onbekend: uitgegaan van 1,5 m. Vul hem in voor de juiste boordiepte.');
  // Tabel 6 a: 0,5 m onder de kern, of 5,0 m als de verontreiniging dieper dan 5 m reikt.
  const bDiepte = Math.min((kern ?? 150) + 50, 500);
  if (known(p.gwsCm) && p.gwsCm > 500) {
    b += pb; pb = 0; gw = 0;
    notes.push('Grondwater dieper dan 5 m: de peilbuis wordt een boring tot 0,5 m onder de kern, minimaal 2 m (tabel 6 d).');
  }
  return {
    borings: [{ depthCm: pb ? bDiepte : Math.max(bDiepte, 200), count: b, label: 'tot 0,5 m onder de kern' }],
    peilbuizen: pb,
    peilbuisDepthCm: pb ? Math.max(bDiepte, peilbuisOpGws(p.gwsCm, 500)) : null,
    soilAnalyses: [{ label: 'grond (kern)', count: grond }],
    waterAnalyses: gw,
    notes,
  };
}

// ── Tabel 7 en 8 — VEP-OO (ondergrondse opslagtanks) ────────────────────
// Kolommen: boringen tot 0,5 m onder de onderzijde van de tank · én boring met peilbuis ·
// grond (tank) · grondwater. Vulpunt en ontluchtingspunt: elk 1 boring tot 1,0 m en 1 monster.
// Tabel 7 gaat naar de inhoud van één tank, tabel 8 naar de totale inhoud van een cluster.
const TABEL_7: [number, number[]][] = [
  [3, [1, 1, 1, 1]], [5, [1, 1, 1, 1]], [10, [2, 1, 2, 1]], [20, [2, 1, 2, 1]], [50, [3, 1, 2, 1]], [Infinity, [3, 1, 2, 1]],
];
const TABEL_8: [number, number[]][] = [
  [10, [2, 1, 1, 1]], [25, [2, 1, 1, 1]], [50, [3, 1, 2, 1]], [100, [3, 1, 3, 1]], [Infinity, [3, 2, 3, 2]],
];

function vepOo(_bronnen: number, p: PlanParams): PlanCounts {
  const notes: string[] = [];
  const meer = known(p.tankCount) && p.tankCount >= 2;
  const tabel = meer ? '8' : '7';
  // Inhoud onbekend: de kleinste rij (zo ook bij een olie-waterscheider, tabel 7 e).
  if (!known(p.tankVolumeM3)) notes.push(`Tankinhoud onbekend: uitgegaan van de kleinste inhoud in tabel ${tabel}.`);
  const v = known(p.tankVolumeM3) ? p.tankVolumeM3 : 0;
  let [bTank, pb, grond, gw] = (meer ? TABEL_8 : TABEL_7).find(([tot]) => v <= tot)![1];

  const tank = known(p.tankBottomCm) ? p.tankBottomCm : null;
  if (tank == null) notes.push('Onderzijde van de tank onbekend: uitgegaan van 2,5 m. Vul hem in voor de juiste boordiepte.');
  const tankDiepte = (tank ?? 250) + 50;

  // Leidingen van meer dan 5 m: één boring per 5 m (tot 0,5 m onder de leiding), één monster per
  // twee boringen met minimaal één (§ 5.4.2.1, tabel 7/8 opmerking). Leidingdiepte: niet gevraagd,
  // 1,0 m aangenomen.
  const leiding = known(p.pipeLengthM) ? p.pipeLengthM : 0;
  const bLeiding = leiding > 5 ? op(leiding / 5) : 0;
  const grondLeiding = bLeiding ? Math.max(1, op(bLeiding / 2)) : 0;
  if (bLeiding) notes.push('Leidingen: uitgegaan van een leiding op 1,0 m; de boringen gaan tot 1,5 m.');

  // Tabel 7/8 d: grondwater dieper dan 5 m → geen peilbuis, wel een boring tot 5 m.
  let bDroog = 0;
  if (known(p.gwsCm) && p.gwsCm > 500) {
    bDroog = pb; pb = 0; gw = 0;
    notes.push(`Grondwater dieper dan 5 m: de peilbuis wordt een boring tot 5 m (tabel ${tabel} d).`);
  }
  notes.push(`Vul- of ontluchtingspunt binnen 2 m van de tank: die boring mag vervallen (tabel ${tabel} a). Haal hem dan in stap 3 weg.`);
  notes.push('Plaats de peilbuis benedenstrooms van de tank, de boringen zo dicht mogelijk bij de tank en bij vulpunt en ontluchting (§ 5.4.2.1).');
  return {
    borings: [
      { depthCm: tankDiepte, count: bTank, label: 'bij de tank, tot 0,5 m onder de onderzijde' },
      { depthCm: 100, count: 2, label: 'bij vulpunt en ontluchtingspunt, tot 1,0 m' },
      { depthCm: 150, count: bLeiding, label: 'langs de leidingen, tot 0,5 m onder de leiding' },
      { depthCm: 500, count: bDroog, label: 'in plaats van de peilbuis, tot 5 m' },
    ].filter((b) => b.count > 0),
    peilbuizen: pb,
    // Bovenkant filter 0,5 m onder de grondwaterspiegel, filter 1 m (§ 5.4.2.2); minimaal tot onder de tank.
    peilbuisDepthCm: pb ? Math.max(tankDiepte, peilbuisOpGws(p.gwsCm, 500)) : null,
    soilAnalyses: [
      { label: 'grond (tank)', count: grond },
      { label: 'grond (vulpunt)', count: 1 },
      { label: 'grond (ontluchting)', count: 1 },
      ...(grondLeiding ? [{ label: 'grond (leidingen)', count: grondLeiding }] : []),
    ],
    waterAnalyses: gw,
    notes,
  };
}

// ── Tabel 9 — VED-HO-NL ─────────────────────────────────────────────────
// Kolommen: boring tot 0,5 m onder de kern · én boring met peilbuis · grond · grondwater.
function vedHoNl(m2: number, p: PlanParams): PlanCounts {
  const ha = m2 / 10000;
  let [b, pb, grond, gw] = ha < 1 ? [5, 2, 2, 2]
    : [...rondAf([5 + ha, 2 + 0.5 * ha], 7 + 1.5 * ha), ...rondAf([2 + 0.5 * ha, 2 + 0.5 * ha])];
  const notes: string[] = [];
  const kern = known(p.suspectBottomCm) ? p.suspectBottomCm : null;
  if (kern == null) notes.push('Onderzijde van de verdachte laag onbekend: uitgegaan van 1,5 m.');
  const bDiepte = (kern ?? 150) + 50;
  if (known(p.gwsCm) && p.gwsCm > 500) {
    b += pb; pb = 0; gw = 0;
    notes.push('Grondwater dieper dan 5 m: peilbuizen worden boringen tot ten minste 5 m (tabel 9 a).');
  }
  return {
    borings: [{ depthCm: pb ? bDiepte : Math.max(bDiepte, 500), count: b, label: 'tot 0,5 m onder de kern' }],
    peilbuizen: pb,
    // Bovenkant filter 0,5 m onder de verdachte laag, tenzij het grondwater dieper zit (§ 5.5.2.2).
    peilbuisDepthCm: pb ? Math.max(bDiepte + 100, known(p.gwsCm) ? p.gwsCm + 150 : 0) : null,
    soilAnalyses: [{ label: 'grond (verdachte laag)', count: grond }],
    waterAnalyses: gw,
    notes,
  };
}

// ── Tabel 11 — VED-HE-NL ────────────────────────────────────────────────
// Kolommen: boring tot 0,5 m in de verdachte laag · én boring tot de onderzijde (max 2 m) ·
// én boring met peilbuis · grond (verdachte laag) · grondwater.
const TABEL_11: [number, number[]][] = [
  [0.01, [2, 1, 1, 1, 1]], [0.05, [3, 1, 1, 2, 1]], [0.10, [5, 1, 1, 3, 1]], [0.15, [7, 1, 1, 3, 1]],
  [0.20, [10, 2, 1, 3, 1]], [0.30, [11, 2, 1, 3, 1]], [0.40, [12, 2, 1, 3, 1]], [0.50, [14, 3, 1, 3, 1]],
  [0.70, [15, 3, 1, 3, 1]], [0.90, [17, 4, 2, 4, 2]], [1.00, [18, 4, 2, 4, 2]],
];

function vedHeNl(m2: number, p: PlanParams): PlanCounts {
  const ha = m2 / 10000;
  let [b05, bOnder, pb, grond, gw] = ha <= 1 ? rij(TABEL_11, ha)!
    : [...rondAf([9 + 9 * ha, 2 + 2 * ha, 1 + ha], 12 + 12 * ha), ...rondAf([2 + 2 * ha, 1 + ha])];
  const notes: string[] = [];
  const boven = known(p.suspectTopCm) ? p.suspectTopCm : 0;
  const onder = known(p.suspectBottomCm) ? p.suspectBottomCm : null;
  if (onder == null) notes.push('Onderzijde van de verdachte laag onbekend: uitgegaan van 1,5 m.');
  const laagOnder = onder ?? 150;
  if (known(p.gwsCm) && p.gwsCm > 500) {
    bOnder += pb; pb = 0; gw = 0;
    notes.push('Grondwater dieper dan 5 m: peilbuizen worden boringen tot ten minste 5 m (tabel 11 a).');
  }
  return {
    borings: [
      { depthCm: boven + 50, count: b05, label: 'tot 0,5 m in de verdachte laag' },
      // Tot de onderzijde, maximaal 2 m onder de bovenzijde van de verdachte laag (§ 5.6.2.2).
      { depthCm: pb ? Math.min(laagOnder, boven + 200) : 500, count: bOnder, label: 'tot onderzijde verdachte laag (max 2 m)' },
    ].filter((b) => b.count > 0),
    peilbuizen: pb,
    // Peilbuisboringen tot 0,5 m onder de verdachte laag; filter daaronder, of op het grondwater.
    peilbuisDepthCm: pb ? Math.max(laagOnder + 150, known(p.gwsCm) ? p.gwsCm + 150 : 0) : null,
    soilAnalyses: [{ label: 'grond (verdachte laag)', count: grond }],
    waterAnalyses: gw,
    notes,
  };
}

export const NEN5740_STRATEGIES: StrategyRule[] = [
  {
    code: 'ONV-NL', label: 'Onverdacht, niet-lijnvormig',
    description: 'Geen aanwijzingen voor bodembelasting; een terrein met een oppervlakte.',
    source: 'NEN 5740 § 5.1, tabel 2', measure: 'area_m2', layout: 'grid', params: ['gwsCm'], compute: onvNl,
  },
  {
    code: 'ONV-L', label: 'Onverdacht, lijnvormig',
    description: 'Geen aanwijzingen voor bodembelasting; een tracé (weg, leiding, sloot). Puntbronnen langs het tracé apart volgens VEP.',
    source: 'NEN 5740 § 5.1, tabel 3', measure: 'length_m', layout: 'line', params: ['excavationCm', 'gwsCm'],
    compute: trace([[1000, 50, 0, 0.05], [10000, 100, 10, 0.1]], '3'),
  },
  {
    code: 'VEP', label: 'Verdacht, plaatselijke kern',
    description: 'Verdacht op een duidelijke kern (morsing, lekkage, brandplaats). Teken de kern; de aantallen gelden per kern (max 0,1 ha).',
    source: 'NEN 5740 § 5.3, tabel 6', measure: 'area_m2', layout: 'grid', params: ['suspectBottomCm', 'gwsCm'], compute: vep,
  },
  {
    code: 'VEP-OO', label: 'Verdacht, ondergrondse opslagtank',
    description: 'Eén of meer ondergrondse tanks (ook olie-waterscheiders en verwijderde tanks). Teken een vlak om de tankput; de boringen komen rond het midden. De afleverinstallatie hoort hier niet bij: die gaat volgens VEP.',
    source: 'NEN 5740 § 5.4, tabel 7 en 8', measure: 'sources', layout: 'source',
    params: ['tankCount', 'tankVolumeM3', 'tankBottomCm', 'pipeLengthM', 'gwsCm'], compute: vepOo,
  },
  {
    code: 'VED-HO-NL', label: 'Verdacht, diffuus homogeen, vlak',
    description: 'Diffuse belasting zonder duidelijke kern, homogeen verdeeld (bijv. ophoging met slib, atmosferische depositie).',
    source: 'NEN 5740 § 5.5, tabel 9', measure: 'area_m2', layout: 'grid', params: ['suspectBottomCm', 'gwsCm'], compute: vedHoNl,
  },
  {
    code: 'VED-HO-L', label: 'Verdacht, diffuus homogeen, tracé',
    description: 'Als VED-HO, voor een tracé.',
    source: 'NEN 5740 § 5.5, tabel 10', measure: 'length_m', layout: 'line', params: ['excavationCm', 'gwsCm'],
    compute: trace([[1000, 50, 0, 0.05], [10000, 100, 10, 0.1], [Infinity, 200, 60, 0.2]], '10'),
  },
  {
    code: 'VED-HE-NL', label: 'Verdacht, diffuus heterogeen, vlak',
    description: 'Diffuse belasting zonder duidelijke kern, heterogeen verdeeld (bijv. stedelijke ophoging, stortingen).',
    source: 'NEN 5740 § 5.6, tabel 11', measure: 'area_m2', layout: 'grid', params: ['suspectTopCm', 'suspectBottomCm', 'gwsCm'], compute: vedHeNl,
  },
  {
    code: 'VED-HE-L', label: 'Verdacht, diffuus heterogeen, tracé',
    description: 'Als VED-HE, voor een tracé.',
    source: 'NEN 5740 § 5.6, tabel 12', measure: 'length_m', layout: 'line', params: ['excavationCm', 'gwsCm'],
    compute: trace([[Infinity, 50, 0, 0.05]], '12'),
  },
];

/** Hoe de opgaven in het scherm heten. */
export const PLAN_PARAM_LABELS: Record<PlanParamKey, string> = {
  gwsCm: 'Grondwaterstand',
  excavationCm: 'Ontgravingsdiepte',
  suspectTopCm: 'Bovenzijde verdachte laag',
  suspectBottomCm: 'Onderzijde kern / verdachte laag',
  tankVolumeM3: 'Inhoud tank(s)',
  tankCount: 'Aantal tanks',
  tankBottomCm: 'Onderzijde tank',
  pipeLengthM: 'Lengte leidingen naar de tank',
};

/** Eenheid van een opgave: een diepte staat in cm en wordt in m –mv getoond; de rest zoals ingevuld. */
export const PLAN_PARAM_UNITS: Record<PlanParamKey, 'depth' | 'm3' | 'count' | 'm'> = {
  gwsCm: 'depth', excavationCm: 'depth', suspectTopCm: 'depth', suspectBottomCm: 'depth', tankBottomCm: 'depth',
  tankVolumeM3: 'm3', tankCount: 'count', pipeLengthM: 'm',
};

export const strategyRule = (code: string | null | undefined): StrategyRule | null =>
  NEN5740_STRATEGIES.find((s) => s.code === code) || null;

export type PlanForResult =
  | { ok: true; rule: StrategyRule; counts: PlanCounts; totalBorings: number }
  | { ok: false; rule: StrategyRule | null; reason: string };

/** Wat een strategie volgens de norm vraagt bij deze grootte (m² of m) en opgaven. */
export function planFor(code: string | null | undefined, size: number, params: PlanParams = {}): PlanForResult {
  const rule = strategyRule(code);
  if (!rule) return { ok: false, rule: null, reason: 'Kies een strategie.' };
  if (!(Number.isFinite(size) && size > 0)) return { ok: false, rule, reason: 'De grootte van de locatie is onbekend; teken het vlak.' };
  const counts = rule.compute(size, params);
  if (typeof counts === 'string') return { ok: false, rule, reason: counts };
  const totalBorings = counts.borings.reduce((s, b) => s + b.count, 0) + counts.peilbuizen;
  return { ok: true, rule, counts, totalBorings };
}

/**
 * Hoeveel grondmonsters er hoogstens in één mengmonster mogen, per strategie (NEN 5740):
 *   ONV       ten hoogste 10 (§ 5.1.2.3)
 *   VEP       maximaal 5 per kern (§ 5.3.2.3)
 *   VEP-OO    maximaal 3, en alleen bij weinig vluchtige brandstof (§ 5.4.2.3)
 *   VED-HO/HE ten hoogste 4 (§ 5.5.2.3 en 5.6.2.3)
 * Zonder bekende strategie: null (de aanroeper kiest dan zijn eigen standaard).
 */
export function maxPotsPerMixedSample(code: string | null | undefined): number | null {
  const c = String(code || '').toUpperCase();
  if (c.startsWith('ONV')) return 10;
  if (c === 'VEP-OO') return 3;
  if (c.startsWith('VEP')) return 5;
  if (c.startsWith('VED')) return 4;
  return null;
}

/** Een korte regel per strategie over mengen, voor uitleg en voor de mengmonsterassistent. */
export function mixingRuleText(code: string | null | undefined): string | null {
  const n = maxPotsPerMixedSample(code);
  if (n == null) return null;
  const c = String(code || '').toUpperCase();
  if (c === 'VEP-OO') return 'hoogstens 3 monsters per mengmonster, en alleen mengen bij weinig vluchtige brandstof (afgewerkte olie, stookolie, HBO); boringen bij de vulleiding niet mengen met die bij de afleverleiding; de meest verdachte monsters analyseren';
  if (c.startsWith('VEP')) return 'hoogstens 5 monsters per mengmonster, per kern apart; de meest verdachte monsters en zintuiglijk verontreinigde monsters moeten in elk geval geanalyseerd worden';
  if (c.startsWith('VED')) return 'hoogstens 4 monsters per mengmonster, alleen uit de verdachte laag, geen verschillende bodemsoorten';
  return 'hoogstens 10 monsters per mengmonster, laagdikte in principe 0,5 m, geen verschillende bodemsoorten';
}
